import React from 'react';
import { Input } from 'antd';
import { InfoCircleOutlined, SearchOutlined, StarFilled, StarOutlined, WarningOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { AppButton } from '../common/AppButton';
import { AppTag } from '../common/AppTag';
import { AppStockCount } from '../common/AppStockCount';
import { PriceHeldTag } from '../common/PriceHeld';
import { useThemeTokens } from '../../hooks/UseThemeTokens';
import { useContentQuery } from '../../hooks/UseContentWidth';
import {
  BREAKPOINT, CONTENT_BREAKPOINT, FONT_SIZE, FONT_WEIGHT, LAYOUT, LINE_HEIGHT, RADIUS, SPACING, TABLE_CHECKBOX_SIZE, TOUCH_TARGET, type ThemeTokens,
} from '../../constants/constants';
import { displaySellerCode } from '../../types/card';
import { storePriceLabel } from '../../constants/stores';
import type { DecidedRowResult, DecisionListItem } from '../../types/decidedPrice';
import {
  DECISION_KIND_LABEL, HUB_SOURCE_SHORT, decisionKindOf, hubPriceLabel, hubReportPending, hubSourceKind, hubWarningHelp, hubWarningLabel,
  naverShoppingUrl, storePriceDiff, type PendingSave,
} from '../../utils/decidedPrice';

/** PriceHub 경고 배지(감사 card-controltower#362) — 막지 않고 알린다. 툴팁에 뜻. */
export const HubWarningBadges: React.FC<{ t: ThemeTokens; warnings: string[] | undefined }> = ({ t, warnings }) => {
  if (!warnings || warnings.length === 0) return null;
  return (
    <>
      {warnings.map((w) => (
        <span
          key={w}
          data-testid={`hub-warning-${w}`}
          title={hubWarningHelp(w)}
          style={{
            fontSize: FONT_SIZE.xs, color: t.warning, background: t.tintWarning, border: `1px solid ${t.lineWarning}`,
            borderRadius: RADIUS.sm, padding: `0 ${SPACING.xxs}px`, whiteSpace: 'nowrap',
          }}
        >
          <WarningOutlined aria-hidden /> {hubWarningLabel(w)}
        </span>
      ))}
    </>
  );
};

// ─── 판매가 결정 표(감사 card-controltower#341) ─────────────────────────────────────────────────────────────
//
// 왼쪽 표 — 이미지 · 이름(코드 · 저장 결과) · 레어도 · 타 샵 최저가 · 네이버 가격 · <매장> 가격(차이 %) · 재고 · 입력칸 + 저장 ·
// 네이버쇼핑 검색 · 상세 · 즐겨찾기. 줄을 누르면 오른쪽 패널(좁은 폭은 아래 시트)에 판매처 목록이 열린다. 상태는 화면(DecidedPricePage)이 가진다.
//
// 1280 화면의 표 자리는 685px(본문 1,280 − 사이드바 220 − 여백 48 − 스크롤바 15 = 997px 에서 오른쪽 패널 300 · 간격 12 를 뺀 것).
// 첫 열 44px = 체크 상자 누르는 곳(감사 #437), 재고(#447), <매장> 가격 64px(감사 #455 · 세 화면 재설계 F5) — 이름 · 타 샵 · 재고 ·
// 입력 · 검색 · 상세 · 즐겨찾기 칸을 줄여 열 최소 폭 합 684px. 이름 칸이 남는 폭을 받는다.
// 숫자 열(타 샵 · 네이버 · 매장 · 재고)은 오른쪽 정렬 고정 폭, 입력은 7자리(9,999,999)가 다 보이는 96px 입력 + 저장, 검색 · 상세 · 즐겨찾기는 한 묶음 열(2026-11-04).
const COLS_FULL = '44px 36px minmax(80px,1fr) 52px 72px 96px 96px 32px 160px 100px';
// 본문 1,080 미만(1280 표 자리 685px)은 이미지 · 레어도 칸을 빼고(레어도는 이름 줄 옆으로) 고정 열 합 588 + 이름 칸 최소 88 = 676 ≤ 685 (코덱 #865).
// 입력 열 160 = 칸 안 여백 8 + 입력 96 + 간격 4 + 버튼 52('다시 시도' → '재시도' · 로딩 아이콘도 안 넘침, #866).
const COLS_NARROW = '44px minmax(88px,1fr) 76px 92px 84px 32px 160px 100px';
// 넓은 세트(이미지 · 레어도 칸 포함)는 **본문 폭**으로 — 넓은 세트 최소 합(고정 열 688 + 이름 80 = 768)과 판매처 패널(300) · 간격(12)이 함께
// 들어가는 본문 1,080 이상(CT 배치 규칙 L2 — #103 의 본문 1400 문턱은 1440~1724 화면(본문 1,157~1,440)을 좁은 세트로 보였다). 본문 1,400
// 이상은 패널이 360 이라 표 자리가 더 줄지만 1,400 − 360 − 12 = 1,028 ≥ 768 로 여전히 넓은 세트. 재기 전(단위 테스트)에는 화면 폭 1440 질의.
const FULL_TABLE_MIN = 768;
const FULL_TABLE_MIN_CONTENT = FULL_TABLE_MIN + 300 + SPACING.sm;
const useFullTable = (): boolean =>
  useContentQuery((w) => w >= FULL_TABLE_MIN_CONTENT && w - (w >= CONTENT_BREAKPOINT.splitWide ? 360 : 300) - SPACING.sm >= FULL_TABLE_MIN,
    `(min-width: ${BREAKPOINT.wide}px)`);
const won = (n: number | null | undefined): string => (n == null ? '-' : `${n.toLocaleString('ko-KR')}원`);

// 표 체크박스 — 16px 상자를 scale(1.5)로 24px 누르는 곳(스토어 상품 관리와 같은 처리).
// 보이는 상자는 18px(감사 #437 — scale(1.5) 24px 는 휴대폰에서 너무 컸다), 누르는 곳은 감싼 label 이 44px.
const CHECKBOX_STYLE: React.CSSProperties = {
  width: TABLE_CHECKBOX_SIZE, height: TABLE_CHECKBOX_SIZE, margin: 0, cursor: 'pointer',
};
const CHECKBOX_HIT: React.CSSProperties = {
  width: TOUCH_TARGET, height: TOUCH_TARGET, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  cursor: 'pointer', flexShrink: 0,
};

export interface DecidedPriceTableProps {
  items:      DecisionListItem[];
  /** 휴대폰 폭(≤640) — 줄을 카드처럼 세로로 쌓는다. */
  phone:      boolean;
  readOnly:   boolean;
  drafts:     Map<number, string>;
  selected:   Set<number>;
  detailId:   number | null;
  saving:     Set<number>;
  results:    Map<number, DecidedRowResult>;
  pending:    Map<number, PendingSave>;
  favorites:  Set<number>;
  /** 지금 화면의 매장 코드 — <매장> 가격 칸 이름(예: "광주 가격"). */
  storeCode?: string | null;
  /** 결과 칩의 "매장 가격 요청에서 보기" — 없으면 링크를 그리지 않는다(저장 중 등). */
  onGoRequests?: () => void;
  /** 카드별 네이버 재고(CT 카드 목록 stockQuantity, 감사 #447) — 없는 카드는 '-'. */
  stockById?: Map<number, number | null>;
  /** 줄 저장을 막는 사유(일괄 저장 중 · 다른 카드 저장 중 — 백엔드 매장 락이 겹치면 409). 저장 중인 그 줄에는 쓰지 않는다. */
  saveLock:   string | undefined;
  /** 결과 불명 요청을 버리고 새로 결정(스토어 값 확인 뒤 — 확인 창은 화면이 띄운다). */
  onResetPending: (cardId: number) => void;
  /** 서버가 같은 요청을 처리 중(409)이라 잠시 다시 시도를 잠근 카드. */
  busy:       Set<number>;
  onToggleSelect: (cardId: number) => void;
  onToggleAll:    () => void;
  onDraft:        (cardId: number, raw: string) => void;
  onSave:         (cardId: number) => void;
  onOpenDetail:   (cardId: number) => void;
  onToggleFavorite: (cardId: number) => void;
}

/** 줄의 저장 결과 한 줄(네이버 · PriceHub 보고). */
export const DecidedResultLine: React.FC<{
  t: ThemeTokens;
  result: DecidedRowResult | undefined;
  pending: PendingSave | undefined;
  /** 지금 보내는 중 — 먼저 적어 둔 요청 번호가 있어도 '결과 불명' 이 아니라 '저장 중'. */
  saving?: boolean;
  /** 결과 불명 요청을 버리고 새로 결정(없으면 링크를 그리지 않는다). */
  onReset?: () => void;
  /** 휴대폰 — 누르는 곳 44px. */
  phone?: boolean;
  /** PriceHub 확정 대기 칩 옆 "매장 가격 요청에서 보기"(없으면 칩만). */
  onGoRequests?: () => void;
  /** 표 줄 — 줄 바꿈 없이 한 줄 말줄임(전체는 칩 title). */
  oneLine?: boolean;
}> = ({ t, result, pending, saving, onReset, phone, onGoRequests, oneLine }) => {
  const wrapStyle = oneLine ? { flexWrap: 'nowrap' as const, overflow: 'hidden' } : { flexWrap: 'wrap' as const };
  const chip = (color: string, bg: string, text: string, title?: string) => (
    <span
      title={title ?? text}
      style={{
        display: 'inline-block', maxWidth: '100%', minWidth: 0, flexShrink: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        fontSize: FONT_SIZE.xs, lineHeight: LINE_HEIGHT.snug, color, background: bg, borderRadius: RADIUS.sm,
        padding: `0 ${SPACING.xxs}px`,
      }}
    >
      {text}
    </span>
  );

  if (saving) {
    return (
      <div data-testid="decided-result" style={{ display: 'flex', gap: SPACING.xxs, minWidth: 0, ...wrapStyle }}>
        {chip(t.textSecondary, t.bgRaised, '저장 중…')}
      </div>
    );
  }
  // 결과 불명(보관된 요청) — 이번 화면에서 결과를 받기 전(새로고침 뒤)이라도 보인다.
  if (pending && result?.kind === 'busy') {
    return (
      <div data-testid="decided-result" style={{ display: 'flex', gap: SPACING.xxs, minWidth: 0, ...wrapStyle }}>
        {chip(t.warning, t.tintWarning, `이미 처리 중 ${won(pending.price)} — 잠시 뒤 결과 확인`, result.message ?? undefined)}
      </div>
    );
  }
  if (pending && (!result || result.kind === 'unknown')) {
    const why = result?.message ? ` · ${result.message}` : '';
    // 복구 줄(결과 불명 칩 + '스토어 확인 후 새로 결정')은 좁은 칸에서도 잘리면 안 된다 — 이 줄만 줄바꿈 허용(코덱 #867).
    return (
      <div data-testid="decided-result" style={{ display: 'flex', gap: SPACING.xxs, alignItems: 'center', minWidth: 0, flexWrap: 'wrap' }}>
        {result?.hubWarnings && result.hubWarnings.length > 0 && chip(
          t.warning, t.tintWarning, `PriceHub 경고 · ${result.hubWarnings.map(hubWarningLabel).join(' · ')}`,
          result.hubWarnings.map(hubWarningHelp).join('\n'),
        )}
        {chip(t.warning, t.tintWarning, `결과 불명 ${won(pending.price)} — 같은 요청으로 다시 시도`,
          `네이버 반영 여부를 모릅니다(${won(pending.price)})${why}. "다시 시도" 는 같은 요청 번호로 보내 두 번 반영되지 않습니다.`)}
        {onReset && (
          // 서버는 지난 요청이 결과 불명이면 같은 번호로 다시 보내도 결과 불명을 그대로 돌려준다 — 스토어 값을 확인하고 새로 정하는 길.
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onReset(); }}
            style={{
              border: 'none', background: 'transparent', padding: 0, cursor: 'pointer', minHeight: phone ? 44 : undefined,
              fontSize: FONT_SIZE.xs, color: t.accent, textDecoration: 'underline', whiteSpace: 'normal', textAlign: 'left',
            }}
          >
            스토어 확인 후 새로 결정
          </button>
        )}
      </div>
    );
  }
  if (!result) return null;

  // PriceHub 로 간 보고는 확정 대기가 된다 — 확정 전까지 가격 조회 ③ · 자동 반영이 이 카드를 건드리지 않는다(세 화면 재설계 F5).
  // 이미 같은 값이면 reported=true + "확정할 것 없음" 이 함께 온다(#340) — 그때는 대기가 아니다.
  const hubPending = hubReportPending(result.hub);
  const hub = result.hub
    ? hubPending
      ? chip(t.success, t.tintSuccess, 'PriceHub 확정 대기',
        result.hub.message ?? 'PriceHub 에 확정 대기로 보고함 — 확정 · 무시는 PriceHub 에서. 확정 전까지 가격 조회 ③ · 자동 반영이 이 카드를 건드리지 않습니다')
      : result.hub.reported
        ? chip(t.success, t.tintSuccess, `PriceHub 보고됨${result.hub.message ? ` · ${result.hub.message}` : ''}`)
        : chip(t.warning, t.tintWarning, `PriceHub 보고 안 됨${result.hub.message ? ` · ${result.hub.message}` : ''}`)
    : null;
  const goRequests = hubPending && onGoRequests && (
    // 폭 제한 — 1280 표의 이름 칸(최소 80px)에서 옆 칸을 덮었다(코덱 리뷰 잡 #322, 감사 #506). 넘치면 말줄임, 전체는 title.
    <button
      type="button"
      title="매장 가격 요청 화면의 PriceHub 확정 대기 탭에서 보기"
      onClick={(e) => { e.stopPropagation(); onGoRequests(); }}
      style={{
        border: 'none', background: 'transparent', padding: 0, cursor: 'pointer', minHeight: phone ? 44 : undefined,
        fontSize: FONT_SIZE.xs, color: t.accent, textDecoration: 'underline', whiteSpace: 'nowrap',
        display: 'inline-block', maxWidth: '100%', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', textAlign: 'left',
      }}
    >
      매장 가격 요청에서 보기
    </button>
  );

  return (
    <div data-testid="decided-result" style={{ display: 'flex', gap: SPACING.xxs, alignItems: 'center', minWidth: 0, ...wrapStyle }}>
      {result.hubWarnings && result.hubWarnings.length > 0 && chip(
        t.warning, t.tintWarning, `PriceHub 경고 · ${result.hubWarnings.map(hubWarningLabel).join(' · ')}`,
        result.hubWarnings.map(hubWarningHelp).join('\n'),
      )}
      {result.kind === 'success' && chip(t.success, t.tintSuccess, `네이버 반영 ${won(result.price)}`,
        result.decidedAt ? `네이버 반영 ${won(result.price)} · ${dayjs(result.decidedAt).format('MM.DD HH:mm')} 결정` : undefined)}
      {result.kind === 'failed' && chip(t.danger, t.tintDanger, `네이버 반영 실패${result.message ? ` · ${result.message}` : ''}`)}
      {result.kind === 'unknown' && chip(t.warning, t.tintWarning, '결과 불명 — 같은 요청으로 다시 시도')}
      {hub}
      {goRequests}
    </div>
  );
};

const stop = (e: React.SyntheticEvent) => e.stopPropagation();

export const DecidedPriceTable: React.FC<DecidedPriceTableProps> = ({
  items, phone, readOnly, drafts, selected, detailId, saving, results, pending, favorites, stockById, saveLock, busy,
  storeCode, onGoRequests, onToggleSelect, onToggleAll, onDraft, onSave, onOpenDetail, onToggleFavorite, onResetPending,
}) => {
  const t = useThemeTokens();
  const allChecked = items.length > 0 && items.every((i) => selected.has(i.cardId));
  const storeCol = storePriceLabel(storeCode);
  const full = useFullTable();
  const COLS = full ? COLS_FULL : COLS_NARROW;

  const header = !phone && (
    <div
      role="row"
      style={{
        display: 'grid', gridTemplateColumns: COLS, alignItems: 'center', minHeight: 36,
        background: t.bgRaised, borderBottom: `1px solid ${t.border}`,
        position: 'sticky', top: LAYOUT.headerHeight, zIndex: 2,
        fontSize: FONT_SIZE.xs, color: t.textSecondary, fontWeight: FONT_WEIGHT.semibold,
      }}
    >
      <div role="columnheader" style={{ display: 'flex', justifyContent: 'center' }}>
        <label data-testid="decided-check-hit" style={CHECKBOX_HIT}>
          <input type="checkbox" aria-label="이 페이지 전체 선택" checked={allChecked} disabled={readOnly || items.length === 0}
            onChange={onToggleAll} style={{ ...CHECKBOX_STYLE, accentColor: t.accent }} />
        </label>
      </div>
      {full && <div role="columnheader" aria-label="이미지" />}
      <div role="columnheader" style={{ padding: `0 ${SPACING.xs}px` }}>카드</div>
      {full && <div role="columnheader" style={{ padding: `0 ${SPACING.xxs}px`, whiteSpace: 'nowrap' }}>레어도</div>}
      <div role="columnheader" style={{ padding: `0 ${SPACING.xxs}px`, textAlign: 'right', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>타 샵 최저가</div>
      <div role="columnheader" style={{ padding: `0 ${SPACING.xxs}px`, textAlign: 'right', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>네이버 가격</div>
      <div role="columnheader" title={`${storeCol} — PriceHub 의 이 매장 판매가(괄호는 네이버 가격과의 차이)`}
        style={{ padding: `0 ${SPACING.xxs}px`, textAlign: 'right', whiteSpace: 'nowrap' }}>{storeCol}</div>
      <div role="columnheader" style={{ padding: `0 ${SPACING.xxs}px`, textAlign: 'right', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>재고</div>
      <div role="columnheader" style={{ padding: `0 ${SPACING.xs}px`, whiteSpace: 'nowrap' }}>판매가 입력</div>
      <div role="columnheader" title="네이버쇼핑 검색 · 상세 · 즐겨찾기" style={{ textAlign: 'center', whiteSpace: 'nowrap' }}>바로가기</div>
    </div>
  );

  return (
    <div role="table" aria-label="판매가 결정 목록" style={{ border: `1px solid ${t.border}`, borderRadius: RADIUS.lg, background: t.bgSurface }}>
      {header}
      {items.map((it) => (
        <DecidedPriceRow
          key={it.cardId}
          t={t}
          item={it}
          phone={phone}
          full={full}
          readOnly={readOnly}
          draft={drafts.get(it.cardId)}
          checked={selected.has(it.cardId)}
          active={detailId === it.cardId}
          saving={saving.has(it.cardId)}
          saveLock={saveLock}
          busy={busy.has(it.cardId)}
          result={results.get(it.cardId)}
          pending={pending.get(it.cardId)}
          favorite={favorites.has(it.cardId)}
          stock={stockById?.get(it.cardId)}
          storeCode={storeCode ?? null}
          onGoRequests={onGoRequests}
          onToggleSelect={onToggleSelect}
          onDraft={onDraft}
          onSave={onSave}
          onOpenDetail={onOpenDetail}
          onToggleFavorite={onToggleFavorite}
          onResetPending={onResetPending}
        />
      ))}
    </div>
  );
};

interface RowProps {
  t: ThemeTokens;
  item: DecisionListItem;
  phone: boolean;
  /** 넓은 표(≥1600px) — 이미지 · 레어도 칸이 따로 있다. 아니면 레어도는 이름 줄 옆. */
  full: boolean;
  readOnly: boolean;
  draft: string | undefined;
  checked: boolean;
  active: boolean;
  saving: boolean;
  saveLock: string | undefined;
  busy: boolean;
  result: DecidedRowResult | undefined;
  pending: PendingSave | undefined;
  favorite: boolean;
  stock: number | null | undefined;
  storeCode: string | null;
  onGoRequests?: () => void;
  onToggleSelect: (cardId: number) => void;
  onDraft: (cardId: number, raw: string) => void;
  onSave: (cardId: number) => void;
  onOpenDetail: (cardId: number) => void;
  onToggleFavorite: (cardId: number) => void;
  onResetPending: (cardId: number) => void;
}

const DecidedPriceRow: React.FC<RowProps> = React.memo(({
  t, item, phone, full, readOnly, draft, checked, active, saving, saveLock, busy, result, pending, favorite, stock, storeCode,
  onGoRequests, onToggleSelect, onDraft, onSave, onOpenDetail, onToggleFavorite, onResetPending,
}) => {
  const id = item.cardId;
  const code = displaySellerCode(item.sellerProductCode);
  const kind = decisionKindOf(item);
  const locked = !!pending;
  // 먼저 적어 둔 요청 번호가 있어도 지금 보내는 중이면 '결과 불명' 이 아니다(Opus 리뷰 중간 4).
  const unknown = locked && !saving;
  const inputValue = locked ? String(pending!.price) : (draft ?? '');
  const saveDisabledReason = readOnly ? '보기 권한 계정은 저장할 수 없습니다'
    : saveLock && !saving ? saveLock
      : busy ? '서버가 같은 요청을 처리 중입니다 — 잠시 뒤 다시 시도하세요'
        : !locked && !inputValue ? '판매가를 입력하세요' : undefined;
  const ctrlHeight = phone ? 44 : 30;

  const image = (w: number, h: number) => (item.imageUrl
    ? <img src={item.imageUrl} alt="" loading="lazy" style={{ width: w, height: h, objectFit: 'cover', borderRadius: RADIUS.sm, border: `1px solid ${t.border}` }}
        onError={(e) => { (e.target as HTMLImageElement).style.visibility = 'hidden'; }} />
    : <div style={{ width: w, height: h, borderRadius: RADIUS.sm, background: t.bgPlaceholder, border: `1px solid ${t.border}` }} />);

  // 체크 칸 둘레 44px 를 누르는 곳으로 — 휴대폰 · PC 모두(#437 · 코덱 리뷰 #66 낮음).
  const checkbox = (
    <label data-testid="decided-check-hit" onClick={stop} style={CHECKBOX_HIT}>
      <input type="checkbox" aria-label={`${item.productName} 선택`} checked={checked} disabled={readOnly}
        onClick={stop} onChange={() => onToggleSelect(id)} style={{ ...CHECKBOX_STYLE, accentColor: t.accent }} />
    </label>
  );

  const priceCell = (
    <div style={{ textAlign: phone ? 'left' : 'right', minWidth: 0 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: phone ? 'flex-start' : 'flex-end', gap: SPACING.xxs }}>
        <span style={{ fontSize: FONT_SIZE.sm, fontWeight: FONT_WEIGHT.semibold, color: item.decidedAt ? t.success : t.textPrimary, fontVariantNumeric: 'tabular-nums' }}>
          {won(item.currentPrice)}
        </span>
        {item.isManual && <AppTag variant="manual" />}
        {item.priceHeld && <PriceHeldTag t={t} testId={`decided-held-${id}`} />}
      </div>
      <div style={{ fontSize: FONT_SIZE.xs, color: t.textSecondary, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
        {item.decidedAt ? `${dayjs(item.decidedAt).format('MM.DD HH:mm')} 결정` : '미설정'}
      </div>
    </div>
  );

  // <매장> 가격(감사 #455, 세 화면 재설계 F5) — PriceHub 가 이 매장에 준 값과 네이버 가격의 차이. 매장 값이 아니면 흐리게 쓰고
  // 출처(공통가 · 매장 미인식 · 출처 없음)를 칸 안에 글자로 — 툴팁에만 두지 않는다(코덱 화면 점검 PR #74 중간). 차이는 네이버 가격 기준 %.
  const sourceKind = hubSourceKind(item.hubPriceSource);
  const storeHub = sourceKind === 'store';
  const diff = storePriceDiff(item.hubPrice, item.currentPrice);
  const hubName = hubPriceLabel(item.hubPriceSource, storeCode);
  const storePriceCell = (
    <div
      data-testid={`decided-store-price-${id}`}
      title={item.hubPrice == null ? `${storePriceLabel(storeCode)} 없음`
        : `${hubName} ${won(item.hubPrice)}${diff ? ` · 네이버 가격보다 ${diff.diff === 0 ? '같음' : `${Math.abs(diff.diff).toLocaleString('ko-KR')}원 ${diff.diff > 0 ? '높음' : '낮음'}`}` : ''}`}
      style={{ textAlign: phone ? 'left' : 'right', minWidth: 0, fontVariantNumeric: 'tabular-nums' }}
    >
      {item.hubPrice == null ? <span style={{ color: t.textEmpty, fontSize: FONT_SIZE.sm }}>-</span> : (
        <>
          <div style={{ fontSize: FONT_SIZE.sm, color: storeHub ? t.textPrimary : t.textMuted, fontStyle: storeHub ? undefined : 'italic', whiteSpace: 'nowrap' }}>
            {won(item.hubPrice)}
          </div>
          {(diff || !storeHub) && (
            <div style={{ fontSize: FONT_SIZE.xs, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', color: t.textSecondary }}>
              {diff && (
                <span style={{ color: diff.diff === 0 ? t.textSecondary : diff.diff > 0 ? t.success : t.danger }}>{diff.text}</span>
              )}
              {diff && !storeHub && ' · '}
              {!storeHub && <span data-testid={`decided-store-source-${id}`}>{HUB_SOURCE_SHORT[sourceKind]}</span>}
            </div>
          )}
        </>
      )}
    </div>
  );

  const marketCell = (
    <div
      style={{ textAlign: phone ? 'left' : 'right', fontSize: FONT_SIZE.sm, color: t.textSecondary, fontVariantNumeric: 'tabular-nums' }}
      title={item.market ? `판매처 ${item.market.shopCount ?? 0}곳${item.market.collectedAt ? ` · ${dayjs(item.market.collectedAt).format('MM.DD HH:mm')} 수집` : ''}` : '타 샵 시세 없음'}
    >
      {item.market?.min != null ? won(item.market.min) : <span style={{ color: t.textEmpty }}>-</span>}
    </div>
  );

  const editor = (
    <div onClick={stop} style={{ display: 'flex', alignItems: 'center', gap: SPACING.xxs, minWidth: 0 }}>
      <Input
        aria-label={`${item.productName} 판매가`}
        inputMode="numeric"
        size={phone ? 'large' : 'small'}
        value={inputValue}
        disabled={readOnly || locked || saving}
        placeholder={item.currentPrice != null ? String(item.currentPrice) : '가격'}
        onChange={(e) => onDraft(id, e.target.value)}
        onPressEnter={() => { if (!saveDisabledReason && !saving) onSave(id); }}
        style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', height: ctrlHeight, minWidth: 0, ...(phone ? { flex: 1 } : { width: 96, flex: 'none' }) }}
      />
      <AppButton
        size={phone ? 'lg' : 'sm'}
        variant={unknown ? 'ghost-danger' : 'primary'}
        loading={saving}
        disabled={!!saveDisabledReason}
        disabledReason={saveDisabledReason}
        onClick={() => onSave(id)}
        aria-label={unknown ? '다시 시도' : undefined}
        style={{ flexShrink: 0, height: ctrlHeight, minWidth: phone ? undefined : 44, paddingInline: phone ? undefined : SPACING.xxs }}
      >
        {unknown ? (phone ? '다시 시도' : '재시도') : '저장'}
      </AppButton>
    </div>
  );

  const searchLink = (
    <a
      href={naverShoppingUrl(item.productName)}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={`${item.productName} 네이버쇼핑에서 검색`}
      title="네이버쇼핑에서 검색"
      onClick={stop}
      style={{
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        width: ctrlHeight, height: ctrlHeight, borderRadius: RADIUS.md, border: `1px solid ${t.border}`,
        color: t.accent, background: t.bgRaised,
      }}
    >
      <SearchOutlined />
    </a>
  );

  const detailButton = phone ? (
    <AppButton size="lg" onClick={(e) => { e.stopPropagation(); onOpenDetail(id); }}
      aria-label={`${item.productName} 상세`} style={{ height: ctrlHeight }}>
      상세
    </AppButton>
  ) : (
    <button
      type="button"
      aria-label={`${item.productName} 상세`}
      title="상세 · 판매처 목록 열기"
      onClick={(e) => { e.stopPropagation(); onOpenDetail(id); }}
      style={{
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
        width: ctrlHeight, height: ctrlHeight, borderRadius: RADIUS.md, border: `1px solid ${t.border}`,
        color: t.textPrimary, background: t.bgRaised, padding: 0,
      }}
    >
      <InfoCircleOutlined />
    </button>
  );

  const favButton = (
    <button
      type="button"
      aria-label={favorite ? `${item.productName} 즐겨찾기 해제` : `${item.productName} 즐겨찾기`}
      aria-pressed={favorite}
      onClick={(e) => { e.stopPropagation(); onToggleFavorite(id); }}
      style={{
        width: phone ? 44 : ctrlHeight, height: phone ? 44 : ctrlHeight, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        border: 'none', background: 'transparent', cursor: 'pointer', color: favorite ? t.warning : t.textMuted, padding: 0,
      }}
    >
      {favorite ? <StarFilled /> : <StarOutlined />}
    </button>
  );

  const kindTag = kind !== 'normal' && (
    <span style={{
      fontSize: FONT_SIZE.xs, color: t.accent, border: `1px solid ${t.lineAccent}`, borderRadius: RADIUS.sm,
      padding: `0 ${SPACING.xxs}px`, whiteSpace: 'nowrap',
    }}>
      {typeof item.mirror === 'string' && item.mirror.trim() ? item.mirror : DECISION_KIND_LABEL[kind]}
    </span>
  );

  const nameBlock = (
    <div style={{ minWidth: 0, display: 'flex', flexDirection: 'column' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: SPACING.xxs, minWidth: 0 }}>
        <span title={item.productName} style={{
          fontSize: FONT_SIZE.sm, fontWeight: FONT_WEIGHT.semibold, color: t.textPrimary,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>
          {item.productName}
        </span>
        {kindTag}
        <HubWarningBadges t={t} warnings={item.hubWarnings} />
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: SPACING.xxs, minWidth: 0 }}>
        {!phone && !full && <AppTag variant="rarity" code={item.rarity} />}
        <span style={{ fontSize: FONT_SIZE.xs, color: t.textMuted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {code || <span style={{ fontStyle: 'italic' }}>코드 없음</span>}
        </span>
      </div>
      <DecidedResultLine
        t={t} result={result} pending={pending} saving={saving} phone={phone} onGoRequests={onGoRequests} oneLine={!phone}
        onReset={!readOnly && !busy && !saveLock && unknown ? () => onResetPending(id) : undefined}
      />
    </div>
  );

  const rowStyle: React.CSSProperties = {
    borderBottom: `1px solid ${t.borderSubtle}`,
    background: active ? t.tintInfo : checked ? t.bgRaised : t.bgSurface,
    boxShadow: active ? `inset 3px 0 0 ${t.accent}` : undefined,
    cursor: 'pointer',
  };

  if (phone) {
    return (
      <div role="row" data-testid={`decided-row-${id}`} aria-selected={active} onClick={() => onOpenDetail(id)}
        style={{ ...rowStyle, display: 'flex', gap: SPACING.xs, padding: SPACING.xs }}>
        <div role="cell" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: SPACING.xs, paddingTop: SPACING.xxs }}>
          {checkbox}
          {image(40, 56)}
        </div>
        <div role="cell" style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: SPACING.xxs }}>
          {nameBlock}
          <div><AppTag variant="rarity" code={item.rarity} /></div>
          {/* 가격 격자(코덱 화면 점검 PR #74 중간) — 라벨과 값을 한 덩어리로 묶어 2열로. 따로 줄바꿈돼 어느 값의 라벨인지 갈라지지 않게. */}
          <div data-testid={`decided-phone-prices-${id}`}
            style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: `${SPACING.xxs}px ${SPACING.sm}px` }}>
            {([
              ['타 샵 최저', marketCell],
              ['네이버 가격', priceCell],
              [storePriceLabel(storeCode), storePriceCell],
              ['재고', <AppStockCount stock={stock} testId={`decided-stock-${id}`} />],
            ] as const).map(([label, value]) => (
              <div key={label} style={{ minWidth: 0 }}>
                <div style={{ fontSize: FONT_SIZE.xs, color: t.textMuted }}>{label}</div>
                {value}
              </div>
            ))}
          </div>
          {editor}
          <div style={{ display: 'flex', alignItems: 'center', gap: SPACING.xs }}>
            {searchLink}
            {detailButton}
            {favButton}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div role="row" data-testid={`decided-row-${id}`} aria-selected={active} onClick={() => onOpenDetail(id)}
      style={{ ...rowStyle, display: 'grid', gridTemplateColumns: full ? COLS_FULL : COLS_NARROW, alignItems: 'center', minHeight: 40 }}>
      <div role="cell" style={{ display: 'flex', justifyContent: 'center' }}>{checkbox}</div>
      {full && <div role="cell" style={{ display: 'flex', justifyContent: 'center', padding: `${SPACING.xxs}px 0` }}>{image(30, 42)}</div>}
      <div role="cell" style={{ padding: `${SPACING.xxs}px ${SPACING.xs}px`, minWidth: 0 }}>{nameBlock}</div>
      {full && <div role="cell" style={{ padding: `0 ${SPACING.xxs}px` }}><AppTag variant="rarity" code={item.rarity} /></div>}
      <div role="cell" style={{ padding: `0 ${SPACING.xxs}px`, minWidth: 0 }}>{marketCell}</div>
      <div role="cell" style={{ padding: `0 ${SPACING.xxs}px`, minWidth: 0 }}>{priceCell}</div>
      <div role="cell" style={{ padding: `0 ${SPACING.xxs}px`, minWidth: 0 }}>{storePriceCell}</div>
      <div role="cell" style={{ padding: `0 ${SPACING.xxs}px`, textAlign: 'right', whiteSpace: 'nowrap' }}><AppStockCount stock={stock} testId={`decided-stock-${id}`} /></div>
      <div role="cell" style={{ padding: `0 ${SPACING.xxs}px` }}>{editor}</div>
      <div role="cell" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: SPACING.xxs }}>
        {searchLink}
        {detailButton}
        {favButton}
      </div>
    </div>
  );
});
DecidedPriceRow.displayName = 'DecidedPriceRow';
